import React, { createContext, useContext, ReactNode, useState } from "react";
import {
  useConversation,
  useConversations,
  useAskQuestion,
  useAskAgent,
  useQuotaStatus,
  type Conversation,
} from "@agentic_conversation/api/conversation";
import { useChatSocket, type DailyUsageStatus } from "@agentic_conversation/hooks/use-chat-socket";

interface UsageData {
  input_cost: number;
  output_cost: number;
  total: number;
  input_tokens?: number;
  output_tokens?: number;
}

interface ThrottleState {
  retryAfter: number;
  startedAt: number;
}

interface SharedChatContextValue {
  conversationId: number;
  conversation: Conversation | undefined;
  conversations: Conversation[] | undefined;
  isLoading: boolean;
  askQuestion: ReturnType<typeof useAskQuestion>;
  askAgent: ReturnType<typeof useAskAgent>;
  throttle: ThrottleState | null;
  dailyUsage: DailyUsageStatus | null;
  usageLimitModal: {
    isOpen: boolean;
    variant: "no_subscription" | "upgrade_plan";
    usageData?: UsageData;
    open: (variant: "no_subscription" | "upgrade_plan", usage?: UsageData) => void;
    close: () => void;
  };
}

const SharedChatContext = createContext<SharedChatContextValue | null>(null);

export function SharedChatProvider({
  children,
  conversationId,
}: {
  children: ReactNode;
  conversationId: number;
}) {
  const { data: conversation, isLoading } = useConversation(conversationId);
  const { data: conversations } = useConversations();
  const askQuestion = useAskQuestion(conversationId);
  const askAgent = useAskAgent(conversationId);

  // Usage limit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVariant, setModalVariant] = useState<"no_subscription" | "upgrade_plan">("no_subscription");
  const [usageData, setUsageData] = useState<UsageData | undefined>();

  // Initialize socket connection and get throttle/daily usage state
  const { throttle, dailyUsage } = useChatSocket(conversationId);

  // Check quota on load — show paywall immediately if exceeded
  const { data: quotaStatus } = useQuotaStatus();
  const quotaExceeded = quotaStatus?.exceeded ?? false;

  const value: SharedChatContextValue = {
    conversationId,
    conversation,
    conversations,
    isLoading,
    askQuestion,
    askAgent,
    throttle,
    dailyUsage,
    usageLimitModal: {
      isOpen: modalOpen || quotaExceeded,
      variant: quotaExceeded ? "no_subscription" : modalVariant,
      usageData,
      open: (variant, usage) => {
        setModalVariant(variant);
        setUsageData(usage);
        setModalOpen(true);
      },
      close: () => setModalOpen(false),
    },
  };

  return (
    <SharedChatContext.Provider value={value}>
      {children}
    </SharedChatContext.Provider>
  );
}

export function useSharedChat(): SharedChatContextValue {
  const context = useContext(SharedChatContext);
  if (!context) {
    throw new Error("useSharedChat must be used within SharedChatProvider");
  }
  return context;
}
